import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";
import {
  LoudspeakersModal,
  StudioRoomModal,
  HeadphonesModal,
  MonitoringControllerModal,
  AudioInterfaceModal,
  VirtualisationModal,
  DawModal,
  AcousticsModal,
  MicrophonesModal,
} from "./Modals/Modals";

import { messages } from "./Workspace.messages";
import styles from "./Workspace.module.scss";

import config from "appConfig";
import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";
import { useUi, useBlocks } from "@jelly/lesson";
import {
  ControlRoomDiagram,
  ControlRoomLabelItem,
} from "@src/lms/jelly/extensions/layouts/_lesson/guide02/workspace/ControlRoomDiagram/ControlRoomDiagram";

const ACCORDION_IDS = {
  controlRoom: "controlRoom",
  recordingRoom: "recordingRoom",
  software: "software",
} as const;

type CaseSlide = "controlRoom" | "recordingRoom" | "software";

interface Case1ContextData {
  setActiveSlide: (slide: CaseSlide) => void;
  activeSlide: CaseSlide;
}

const WorkspaceContext = React.createContext<Case1ContextData>({
  activeSlide: "controlRoom",
  setActiveSlide: () => {
    throw new Error("Not implemented!");
  },
});

interface Workspace {
  title: string;
}

export const Workspace: React.FC<Workspace> = ({ title }) => {
  const [activeSlide, setActiveSlide] = useState<CaseSlide>("controlRoom");

  return (
    <WorkspaceContext.Provider
      value={{
        setActiveSlide,
        activeSlide,
      }}
    >
      <WorkspaceContent title={title} />
    </WorkspaceContext.Provider>
  );
};

const WorkspaceContent: React.FC<Workspace> = ({ title }) => {
  const ui = useUi();
  const blocks = useBlocks();
  const { formatMessage } = useIntl();
  const { activeSlide } = useContext(WorkspaceContext);

  // Modal state management
  const [loudspeakersModalOpen, setLoudspeakersModalOpen] = useState(false);
  const [studioRoomModalOpen, setStudioRoomModalOpen] = useState(false);
  const [headphonesModalOpen, setHeadphonesModalOpen] = useState(false);
  const [monitoringControllerModalOpen, setMonitoringControllerModalOpen] = useState(false);
  const [audioInterfaceModalOpen, setAudioInterfaceModalOpen] = useState(false);
  const [virtualisationModalOpen, setVirtualisationModalOpen] = useState(false);
  const [dawModalOpen, setDawModalOpen] = useState(false);
  const [acousticsModalOpen, setAcousticsModalOpen] = useState(false);
  const [microphonesModalOpen, setMicrophonesModalOpen] = useState(false);

  // Close handlers for modals
  const handleCloseLoudspeakersModal = useCallback(() => setLoudspeakersModalOpen(false), []);
  const handleCloseStudioRoomModal = useCallback(() => setStudioRoomModalOpen(false), []);
  const handleCloseHeadphonesModal = useCallback(() => setHeadphonesModalOpen(false), []);
  const handleCloseMonitoringControllerModal = useCallback(() => setMonitoringControllerModalOpen(false), []);
  const handleCloseAudioInterfaceModal = useCallback(() => setAudioInterfaceModalOpen(false), []);
  const handleCloseVirtualisationModal = useCallback(() => setVirtualisationModalOpen(false), []);
  const handleCloseDawModal = useCallback(() => setDawModalOpen(false), []);
  const handleCloseAcousticsModal = useCallback(() => setAcousticsModalOpen(false), []);
  const handleCloseMicrophonesModal = useCallback(() => setMicrophonesModalOpen(false), []);

  type ModalItem = ControlRoomLabelItem | "acoustics" | "microphones" | "daw" | "virtualisation" | null;

  const handleShowModal = useCallback((item: ModalItem) => {
    switch (item) {
      case "label-headphones":
        setHeadphonesModalOpen(true);
        break;
      case "label-speakers":
        setLoudspeakersModalOpen(true);
        break;
      case "label-interface":
        setAudioInterfaceModalOpen(true);
        break;
      case "label-monitoring-controller":
        setMonitoringControllerModalOpen(true);
        break;
      case "label-studio-room":
        setStudioRoomModalOpen(true);
        break;
      case "acoustics":
        setAcousticsModalOpen(true);
        break;
      case "microphones":
        setMicrophonesModalOpen(true);
        break;
      case "daw":
        setDawModalOpen(true);
        break;
      case "virtualisation":
        setVirtualisationModalOpen(true);
        break;
      default:
        break;
    }
  }, []);

  const accordion = (
    <div className={styles.accordion}>
      <ui.Scrollbars>
        <AccordionItem
          id={ACCORDION_IDS.controlRoom}
          title={formatMessage(messages.G2_P2_S1_A1_Title)}
          unmountChildrenWhenHidden
        >
          <p>{formatMessage(messages.G2_P2_S1_A1_Description)}</p>
        </AccordionItem>
        <AccordionItem
          id={ACCORDION_IDS.recordingRoom}
          title={formatMessage(messages.G2_P2_S1_A2_Title)}
          unmountChildrenWhenHidden
        >
          <p>{formatMessage(messages.G2_P2_S1_A2_Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={() => handleShowModal("acoustics")}>
            {formatMessage(messages.G2_P2_S1_A2_Button1_Label)}
          </ui.Button>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={() => handleShowModal("microphones")}>
            {formatMessage(messages.G2_P2_S1_A2_Button2_Label)}
          </ui.Button>
        </AccordionItem>
        <AccordionItem
          id={ACCORDION_IDS.software}
          title={formatMessage(messages.G2_P2_S1_A3_Title)}
          unmountChildrenWhenHidden
        >
          <p>{formatMessage(messages.G2_P2_S1_A3_Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={() => handleShowModal("daw")}>
            {formatMessage(messages.G2_P2_S1_A3_Button1_Label)}
          </ui.Button>
          <ui.Button
            shape={"normal"}
            theme={"primary"}
            size={"small"}
            onClick={() => handleShowModal("virtualisation")}
          >
            {formatMessage(messages.G2_P2_S1_A3_Button2_Label)}
          </ui.Button>
        </AccordionItem>
      </ui.Scrollbars>
    </div>
  );

  let content: React.ReactNode = activeSlide;

  switch (activeSlide) {
    case "controlRoom":
      content = <ControlRoomDiagram onItemSelect={handleShowModal} />;
      break;
    case "recordingRoom":
      content = <blocks.image src={`${config.STATIC_ASSETS_URL}/lms/guide/guide02/recording-room.png`}></blocks.image>;
      break;
    case "software":
      content = <blocks.image src={`${config.STATIC_ASSETS_URL}/lms/guide/guide02/en/software.svg`}></blocks.image>;
      break;
  }

  return (
    <StandardCase
      title={title}
      leftComponent={
        <Accordion allowMultiOpen={false} defaultOpen={[ACCORDION_IDS.controlRoom]}>
          <MainAccordionObserver />
          {accordion}
        </Accordion>
      }
    >
      <div className={styles.content}>{content}</div>

      {/* Modals */}
      <LoudspeakersModal visible={loudspeakersModalOpen} onClose={handleCloseLoudspeakersModal} />
      <StudioRoomModal visible={studioRoomModalOpen} onClose={handleCloseStudioRoomModal} />
      <HeadphonesModal visible={headphonesModalOpen} onClose={handleCloseHeadphonesModal} />
      <MonitoringControllerModal
        visible={monitoringControllerModalOpen}
        onClose={handleCloseMonitoringControllerModal}
      />
      <AudioInterfaceModal visible={audioInterfaceModalOpen} onClose={handleCloseAudioInterfaceModal} />
      <VirtualisationModal visible={virtualisationModalOpen} onClose={handleCloseVirtualisationModal} />
      <DawModal visible={dawModalOpen} onClose={handleCloseDawModal} />
      <AcousticsModal visible={acousticsModalOpen} onClose={handleCloseAcousticsModal} />
      <MicrophonesModal visible={microphonesModalOpen} onClose={handleCloseMicrophonesModal} />
    </StandardCase>
  );
};

const MainAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(WorkspaceContext);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: CaseSlide = "controlRoom";
      switch (openItem) {
        case ACCORDION_IDS.controlRoom:
          slide = "controlRoom";
          break;
        case ACCORDION_IDS.recordingRoom:
          slide = "recordingRoom";
          break;
        case ACCORDION_IDS.software:
          slide = "software";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
